'use client';

import {
  useEffect,
  type ComponentPropsWithoutRef,
  type FC,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import { DashboardContentCard } from '../DashboardContentCard';
import { twJoin } from 'tailwind-merge';
import { trackEvent } from '@tuic/tracking';
import { getNodeText } from '@tuic/lib/helpers/node-text';
import { Button, type ButtonProps } from '../../../core-components';

//TODO: This component should be integrated into the design system as it's often required.

type CardProps = ComponentPropsWithoutRef<typeof DashboardContentCard>;

type Props = PropsWithChildren<{
  title?: ReactNode;
  text?: ReactNode;
  cards?: CardProps[];
  backButton?: ButtonProps;
}>;

const renderCards = (cards: CardProps[]) => {
  if (cards.length === 0) {
    return null;
  }

  const renderableCards = cards.slice(0, 4);

  const className = twJoin(
    'grid grid-cols-1 gap-y-6 md:gap-x-6 md:gap-y-0',
    renderableCards.length === 2 && 'md:grid-cols-2',
    renderableCards.length === 3 && 'md:grid-cols-3',
    renderableCards.length === 4 && 'md:grid-cols-4',
  );

  return (
    <ul className={className}>
      {renderableCards.map((card, idx) => (
        <li key={idx}>
          <DashboardContentCard {...card} className="h-full" />
        </li>
      ))}
    </ul>
  );
};

/**
 * Render a not found segment with optional cards.
 * You can currently add up to four cards.
 */
export const DashboardNotFound: FC<Props> = ({
  title,
  text,
  cards = [],
  backButton,
}) => {
  useEffect(() => {
    trackEvent({
      event_name: 'error',
      error_type: '404',
      error_request: window.location.href || '',
      error_displayed_message: getNodeText(title),
    });
  }, []);

  return (
    <div className="space-y-12" data-track-module="not-found">
      {(title || text) && (
        <div className="space-y-2 text-center">
          {title && (
            <div className="headline-lg font-semibold text-marine-high-emphasis">
              {title}
            </div>
          )}

          {text && (
            <div className="text-base text-marine-medium-emphasis">{text}</div>
          )}
        </div>
      )}

      {backButton && (
        <div className="flex justify-center">
          <Button {...backButton} />
        </div>
      )}

      {renderCards(cards)}
    </div>
  );
};
